const ASSET_LOAD_ERROR_PATTERN =
  /Unable to preload CSS|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk|_result\.default/i;

const RELOAD_STORAGE_PREFIX = "easil:vite-preload-error-reloaded:";

interface VitePreloadErrorEvent extends Event {
  payload?: unknown;
}

interface ReloadLocation {
  pathname?: string;
  href?: string;
  reload: () => void;
}

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

interface VitePreloadErrorWindow extends EventTarget {
  location: ReloadLocation;
  sessionStorage?: StorageLike;
  __vitePreloadErrorHandlerInstalled__?: boolean;
}

const getPayloadMessage = (payload: unknown): string => {
  if (payload instanceof Error) return payload.message;
  if (typeof payload === "string") return payload;
  if (
    payload &&
    typeof payload === "object" &&
    "message" in payload &&
    typeof payload.message === "string"
  ) {
    return payload.message;
  }

  return "";
};

const getReloadKey = (window: VitePreloadErrorWindow) => {
  const locationKey =
    window.location.pathname || window.location.href || "unknown";
  return `${RELOAD_STORAGE_PREFIX}${locationKey}`;
};

const hasReloadedFor = (window: VitePreloadErrorWindow, key: string) => {
  try {
    return window.sessionStorage?.getItem(key) === "1";
  } catch {
    return false;
  }
};

const markReloadedFor = (window: VitePreloadErrorWindow, key: string) => {
  try {
    if (!window.sessionStorage) return false;
    window.sessionStorage.setItem(key, "1");
    return true;
  } catch {
    return false;
  }
};

export const isViteAssetLoadError = (payload: unknown): boolean =>
  ASSET_LOAD_ERROR_PATTERN.test(getPayloadMessage(payload));

export const reloadForViteAssetLoadError = (
  payload: unknown,
  window: VitePreloadErrorWindow = globalThis.window as VitePreloadErrorWindow
): boolean => {
  const message = getPayloadMessage(payload);

  if (!ASSET_LOAD_ERROR_PATTERN.test(message)) return false;

  const reloadKey = getReloadKey(window);
  if (hasReloadedFor(window, reloadKey)) {
    console.info(
      "Vite asset load failed after a recovery reload; leaving the error visible:",
      message
    );
    return false;
  }

  if (!markReloadedFor(window, reloadKey)) {
    console.info(
      "Vite asset load failed but reload-loop protection is unavailable; leaving the error visible:",
      message
    );
    return false;
  }

  console.info(
    "Vite asset load failed; reloading to fetch the current build:",
    message
  );
  window.location.reload();
  return true;
};

export const registerVitePreloadErrorHandler = (
  window: VitePreloadErrorWindow = globalThis.window as VitePreloadErrorWindow
): void => {
  if (!window || window.__vitePreloadErrorHandlerInstalled__) return;

  window.__vitePreloadErrorHandlerInstalled__ = true;

  window.addEventListener("vite:preloadError", event => {
    const preloadErrorEvent = event as VitePreloadErrorEvent;

    if (!isViteAssetLoadError(preloadErrorEvent.payload)) return;

    if (reloadForViteAssetLoadError(preloadErrorEvent.payload, window)) {
      preloadErrorEvent.preventDefault();
    }
  });
};
